import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'

// ─── Lazy imports ──────────────────────────────────────────────────────────────

const LoginPage          = lazy(() => import('@/pages/LoginPage'))
const DashboardPage      = lazy(() => import('@/pages/DashboardPage'))
const ProducersPage      = lazy(() => import('@/pages/ProducersPage'))
const NewProducerPage    = lazy(() => import('@/pages/NewProducerPage'))
const ProducerDetailPage = lazy(() => import('@/pages/ProducerDetailPage'))
const ProjectsPage       = lazy(() => import('@/pages/ProjectsPage'))
const NewProjectPage     = lazy(() => import('@/pages/NewProjectPage'))
const ProjectDetailPage  = lazy(() => import('@/pages/ProjectDetailPage'))
const AnalysisPage       = lazy(() => import('@/pages/AnalysisPage'))
const ReportsPage        = lazy(() => import('@/pages/ReportsPage'))

// Admin pages
const AdminLayout  = lazy(() => import('@/pages/admin/AdminLayout'))
const AgentsPage   = lazy(() => import('@/pages/admin/AgentsPage'))
const CostsPage    = lazy(() => import('@/pages/admin/CostsPage'))
const UsersPage    = lazy(() => import('@/pages/admin/UsersPage'))
const PromptsPage  = lazy(() => import('@/pages/admin/PromptsPage'))
const LogsPage     = lazy(() => import('@/pages/admin/LogsPage'))

// ─── Loading fallback ─────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]">
      <Loader2 className="h-8 w-8 animate-spin text-agro-500" />
    </div>
  )
}

// ─── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Redirect root */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Protected routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Producers */}
          <Route path="/producers"         element={<ProducersPage />} />
          <Route path="/producers/new"     element={<NewProducerPage />} />
          <Route path="/producers/:id"     element={<ProducerDetailPage />} />

          {/* Projects */}
          <Route path="/projects"               element={<ProjectsPage />} />
          <Route path="/projects/new"           element={<NewProjectPage />} />
          <Route path="/projects/:id"           element={<ProjectDetailPage />} />
          <Route path="/projects/:id/analysis"  element={<AnalysisPage />} />
          <Route path="/projects/:id/reports"   element={<ReportsPage />} />

          {/* Admin – requires ADMIN role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute minRole="ADMIN">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/agents" replace />} />
            <Route path="agents"  element={<AgentsPage />} />
            <Route path="costs"   element={<CostsPage />} />
            <Route path="users"   element={<UsersPage />} />
            <Route path="prompts" element={<PromptsPage />} />
            <Route path="logs"    element={<LogsPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center h-screen gap-4 text-gray-500">
              <p className="text-6xl font-bold text-gray-200">404</p>
              <p className="text-lg">Página não encontrada</p>
              <a href="/dashboard" className="text-agro-600 hover:underline text-sm">
                Voltar ao Dashboard
              </a>
            </div>
          }
        />
      </Routes>
    </Suspense>
  )
}
